import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api, { getAvatarUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePermission } from '../lib/permissions';
import { PRIORITY_LABEL, PRIORITY_COLORS, dueChipClass, fmtDate, initials, priorityKey } from '../lib/constants';
import { AlertCircle, Filter, Check, X, Search, Trash2, ChevronRight, Users, LayoutGrid, Target, ListChecks, Clock, Flame, Zap, FolderKanban, CheckCircle2, Timer } from 'lucide-react';
import TaskDetail from '../components/TaskDetail';

function Avatar({ user, size = 24 }) {
  return (
    <div className="tm-avatar" title={user?.full_name || user?.email} style={{
      width: size, height: size, fontSize: size / 2.5,
      background: user?.avatar_url ? `url(${getAvatarUrl(user.avatar_url)}) center/cover` : (user?.avatar_color || 'var(--trello-text-muted)'),
    }}>{!user?.avatar_url && initials(user)}</div>
  );
}

function fmtHours(s) { if (!s) return '0.0h'; return (s / 3600).toFixed(1) + 'h'; }

export default function CommandCenter() {
  const { user } = useAuth();
  const { has } = usePermission();
  const hasCommandAccess = has('dashboard.view_command');
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [pending, setPending] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState([]);
  const [filters, setFilters] = useState(() => ({
    q: '',
    project: searchParams.get('project') || '',
    priority: searchParams.get('priority') || '',
    assignee: searchParams.get('assignee') || '',
    status: searchParams.get('status') || '',
  }));
  const [openTaskId, setOpenTaskId] = useState(null);
  const [decisionModal, setDecisionModal] = useState(null); // { suggestion, mode: 'approve' | 'reject' }
  const [decisionComment, setDecisionComment] = useState('');
  const [decisionBusy, setDecisionBusy] = useState(false);
  const tableRef = React.useRef(null);
  const pendingRef = React.useRef(null);
  const suggestionsRef = React.useRef(null);

  const scrollToTable = () => setTimeout(() => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 20);
  const scrollToPending = () => setTimeout(() => pendingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 20);
  const scrollToSuggestions = () => setTimeout(() => suggestionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 20);

  const applyKpi = (patch) => {
    setFilters({ q: '', project: '', priority: '', assignee: '', status: '', ...patch });
    scrollToTable();
  };

  const load = async () => {
    try {
      const [t, p, pj, tm, s, pr, ds] = await Promise.all([
        api.get('/tasks'),
        api.get('/tasks/pending-deletions'),
        api.get('/projects'),
        api.get('/team'),
        api.get('/reports/summary'),
        api.get('/reports/project-progress'),
        api.get('/rewards/deletion-suggestions?status=pending'),
      ]);
      setTasks(t.data.tasks); setPending(p.data.requests); setProjects(pj.data.projects);
      setTeam(tm.data.members); setSummary(s.data.summary); setProgress(pr.data.progress);
      setSuggestions(ds.data.suggestions || []);
    } catch (e) {
      // Non-fatal: user may lack a permission for one of the endpoints (e.g. `tasks.decide_deletion`).
      // The `hasCommandAccess` early-return below hides the UI; we just avoid an unhandled rejection.
      console.warn('[CommandCenter] load skipped:', e.message);
    }
  };
  useEffect(() => { if (hasCommandAccess) load(); }, [hasCommandAccess]);

  const filtered = useMemo(() => tasks.filter((t) => {
    if (filters.q && !t.title.toLowerCase().includes(filters.q.toLowerCase())) return false;
    if (filters.project && t.project_id !== filters.project) return false;
    if (filters.priority && priorityKey(t) !== filters.priority) return false;
    if (filters.assignee) {
      if (filters.assignee === 'unassigned' && (t.assignees || []).length > 0) return false;
      if (filters.assignee !== 'unassigned' && !(t.assignees || []).some((a) => a.id === filters.assignee)) return false;
    }
    if (filters.status === 'done' && !t.completed) return false;
    if (filters.status === 'open' && t.completed) return false;
    if (filters.status === 'overdue') {
      if (t.completed) return false;
      if (!t.due_date) return false;
      if (new Date(t.due_date).getTime() > Date.now()) return false;
    }
    if (filters.status === 'due3d') {
      if (t.completed) return false;
      if (!t.due_date) return false;
      if (new Date(t.due_date).getTime() > Date.now() + 3 * 86400000) return false;
    }
    return true;
  }), [tasks, filters]);

  const kpis = useMemo(() => {
    const open = tasks.filter((t) => !t.completed).length;
    const overdue = tasks.filter((t) => !t.completed && t.due_date && new Date(t.due_date) < new Date()).length;
    const dueSoon = tasks.filter((t) => !t.completed && t.due_date && new Date(t.due_date) < new Date(Date.now() + 3 * 86400000)).length;
    const priority_now = tasks.filter((t) => !t.completed && priorityKey(t) === 'critical').length;
    return { open, overdue, dueSoon, priority_now };
  }, [tasks]);

  const byManager = useMemo(() => {
    const map = {};
    projects.forEach((p) => {
      const key = p.delegator_id || 'unassigned';
      const name = p.delegator_name || 'Unassigned';
      (map[key] = map[key] || { name, count: 0, tasks: 0, done: 0 }).count++;
    });
    tasks.forEach((t) => {
      const proj = projects.find((p) => p.id === t.project_id);
      const key = proj?.delegator_id || 'unassigned';
      if (!map[key]) return;
      map[key].tasks++;
      if (t.completed) map[key].done++;
    });
    return Object.values(map);
  }, [projects, tasks]);

  if (!hasCommandAccess) {
    return <div className="p-8 text-center text-white">You don&apos;t have permission to access the Command Center.</div>;
  }

  const decide = async (id, approve) => {
    await api.post(`/tasks/${id}/delete-decision`, { approve });
    load();
  };

  return (
    <div style={{ padding: '24px 24px 80px', maxWidth: 1400, margin: '0 auto' }} data-testid="command-center">
      <div style={{ color: 'var(--trello-text)', marginBottom: 20 }}>
        <div className="flex items-center gap-3">
          <Target size={26} />
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Command Center</h1>
        </div>
        <p style={{ opacity: 0.85, marginTop: 4 }}>Everything happening in your organization at a glance.</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="reports-summary">
          <button type="button" className="tm-stat-tinted" data-testid="rep-card-projects"
            style={{ '--tint': '#0079BF', '--tint-bg': '#E4F0F6' }} onClick={() => nav('/projects')}>
            <div className="flex items-center gap-2 mb-1"><FolderKanban size={16} /><span className="tm-stat-label">Projects</span></div>
            <div className="tm-stat-value">{summary.total_projects}</div>
          </button>
          <button type="button" className="tm-stat-tinted" data-testid="rep-card-tasks"
            style={{ '--tint': '#D29034', '--tint-bg': '#FFF7E6' }} onClick={() => applyKpi({ status: '' })}>
            <div className="flex items-center gap-2 mb-1"><ListChecks size={16} /><span className="tm-stat-label">Tasks</span></div>
            <div className="tm-stat-value">{summary.total_tasks}</div>
          </button>
          <button type="button" className="tm-stat-tinted" data-testid="rep-card-completed"
            style={{ '--tint': '#519839', '--tint-bg': '#E6F4EA' }} onClick={() => applyKpi({ status: 'done' })}>
            <div className="flex items-center gap-2 mb-1"><CheckCircle2 size={16} /><span className="tm-stat-label">Completed</span></div>
            <div className="tm-stat-value">{summary.completed_tasks}</div>
          </button>
          <button type="button" className="tm-stat-tinted" data-testid="rep-card-time"
            style={{ '--tint': '#89609E', '--tint-bg': '#F3ECF7' }} onClick={() => nav('/reports')}>
            <div className="flex items-center gap-2 mb-1"><Timer size={16} /><span className="tm-stat-label">Time Tracked</span></div>
            <div className="tm-stat-value">{fmtHours(summary.total_seconds)}</div>
          </button>
          <button type="button" className="tm-stat-tinted" data-testid="rep-card-active"
            style={{ '--tint': '#B04632', '--tint-bg': '#FBEDEA' }} onClick={() => nav('/team')}>
            <div className="flex items-center gap-2 mb-1"><Users size={16} /><span className="tm-stat-label">Active</span></div>
            <div className="tm-stat-value">{summary.active_members}</div>
          </button>
        </div>
      )}

      {/* KPI ROW */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Kpi label="Open tasks" value={kpis.open} color="#0079BF" bg="#E4F0F6" Icon={ListChecks} testId="kpi-open"
          onClick={() => applyKpi({ status: 'open' })} active={filters.status === 'open'} />
        <Kpi label="Overdue" value={kpis.overdue} color="#EB5A46" bg="#FBEDEA" Icon={AlertCircle} testId="kpi-overdue"
          onClick={() => applyKpi({ status: 'overdue' })} active={filters.status === 'overdue'} />
        <Kpi label="Due in 3d" value={kpis.dueSoon} color="#D29034" bg="#FFF7E6" Icon={Clock} testId="kpi-due3d"
          onClick={() => applyKpi({ status: 'due3d' })} active={filters.status === 'due3d'} />
        <Kpi label="Urgent (Do Now)" value={kpis.priority_now} color="#DE350B" bg="#FFEBE6" Icon={Flame} testId="kpi-urgent"
          onClick={() => applyKpi({ priority: 'now', status: 'open' })} active={filters.priority === 'now'} />
        <Kpi label="Pending Suggestions" value={suggestions.length} color="#7F5A00" bg="#FFF4E6" Icon={Trash2} testId="kpi-suggestions"
          onClick={scrollToSuggestions} disabled={suggestions.length === 0} />
      </div>

      {/* Pending deletion SUGGESTIONS (rich workflow — anyone can propose) */}
      {suggestions.length > 0 && (
        <div className="mb-4" data-testid="suggestions-block" ref={suggestionsRef}>
          <div className="tm-stat-card" style={{ borderLeftColor: '#7F5A00' }}>
            <div className="flex items-center gap-2 mb-3">
              <Trash2 size={18} color="#7F5A00" />
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Pending Deletion Suggestions</h3>
              <span style={{ background: 'var(--trello-list-bg)', color: '#7F5A00', fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>{suggestions.length}</span>
            </div>
            <div className="space-y-2">
              {suggestions.map((s) => (
                <div key={s.id} className="p-3 hover:bg-yellow-50 cursor-pointer transition-colors" style={{ borderRadius: 6, background: 'var(--trello-list-bg)', border: '1px solid #F2C94C' }} data-testid={`sugg-${s.id}`} onClick={() => setOpenTaskId(s.task_id)}>
                  <div className="flex items-start justify-between gap-3">
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }} data-testid={`sugg-title-${s.id}`}>{s.task_title}</div>
                      <div style={{ fontSize: 12, color: 'var(--trello-text-muted)', marginTop: 2 }}>
                        Suggested by <b>{s.requester_name}</b> on {new Date(s.created_at).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: 13, marginTop: 6 }}><b>Reason:</b> {s.reason}</div>
                      {s.business_benefit && <div style={{ fontSize: 13, marginTop: 2 }}><b>Benefit:</b> {s.business_benefit}</div>}
                      <div className="flex gap-3 mt-1" style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>
                        {s.hours_saved != null && <span>⏱ {s.hours_saved}h saved</span>}
                        {s.impact && <span>Impact: {s.impact}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="tm-btn" style={{ background: '#519839' }} onClick={(e) => { e.stopPropagation(); setDecisionModal({ suggestion: s, mode: 'approve' }); setDecisionComment(''); }} data-testid={`sugg-approve-${s.id}`}>
                        <Check size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />Approve
                      </button>
                      <button className="tm-btn-ghost" onClick={(e) => { e.stopPropagation(); setDecisionModal({ suggestion: s, mode: 'reject' }); setDecisionComment(''); }} data-testid={`sugg-reject-${s.id}`}>
                        <X size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pending delete requests */}
      {pending.length > 0 && (
        <div className="mb-4" data-testid="pending-block" ref={pendingRef}>
          <div className="tm-stat-card" style={{ borderLeftColor: '#DE350B' }}>
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle size={18} color="#DE350B" />
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Delete requests awaiting your decision</h3>
            </div>
            <div className="space-y-2">
              {pending.map((t) => (
                <div key={t.id} className="flex items-center gap-3 p-2 hover:bg-red-50 cursor-pointer transition-colors" style={{ borderRadius: 6, background: 'var(--trello-list-bg)' }} data-testid={`pending-${t.id}`} onClick={() => setOpenTaskId(t.id)}>
                  <span className="tm-pill tm-pill-now" style={{ background: t.project_color, color: '#fff' }}>{t.project_name}</span>
                  <div style={{ flex: 1, fontSize: 14 }}>
                    <div style={{ fontWeight: 600 }}>{t.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>Requested by <b>{t.requested_by_name}</b>{t.delete_reason ? ` · "${t.delete_reason}"` : ''}</div>
                  </div>
                  <button className="tm-btn" style={{ background: '#DE350B' }} onClick={(e) => { e.stopPropagation(); decide(t.id, true); }} data-testid={`approve-${t.id}`}>Approve delete</button>
                  <button className="tm-btn-ghost" onClick={(e) => { e.stopPropagation(); decide(t.id, false); }} data-testid={`reject-${t.id}`}>Reject</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)' }} ref={tableRef}>
        {/* MAIN: All tasks with filters */}
        <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>All tasks</h3>
            <span style={{ background: 'var(--trello-list-bg)', fontSize: 11, padding: '2px 8px', borderRadius: 10 }}>{filtered.length}/{tasks.length}</span>
          </div>
          <div className="flex gap-2 flex-wrap mb-3">
            <div style={{ position: 'relative', flex: '1 1 200px' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 12, color: 'var(--trello-text-muted)' }} />
              <input className="tm-input" style={{ paddingLeft: 30 }} placeholder="Search tasks…" value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="cc-search" />
            </div>
            <select className="tm-input" style={{ maxWidth: 180 }} value={filters.project}
              onChange={(e) => setFilters({ ...filters, project: e.target.value })} data-testid="cc-project">
              <option value="">All projects</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select className="tm-input" style={{ maxWidth: 140 }} value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })} data-testid="cc-priority">
              <option value="">Any priority</option>
              {Object.entries(PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="tm-input" style={{ maxWidth: 180 }} value={filters.assignee}
              onChange={(e) => setFilters({ ...filters, assignee: e.target.value })} data-testid="cc-assignee">
              <option value="">Any assignee</option>
              <option value="unassigned">Unassigned</option>
              {team.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
            </select>
            <select className="tm-input" style={{ maxWidth: 140 }} value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="cc-status">
              <option value="">Any status</option>
              <option value="open">Open</option>
              <option value="done">Completed</option>
              <option value="overdue">Overdue</option>
              <option value="due3d">Due in 3d</option>
            </select>
          </div>

          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--trello-text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  <th style={{ padding: '6px 4px' }}>Task</th>
                  <th>Project</th>
                  <th>Priority</th>
                  <th>Assignees</th>
                  <th>Due</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const dc = dueChipClass(t);
                  return (
                    <tr key={t.id} style={{ borderTop: '1px solid var(--trello-border)', cursor: 'pointer' }}
                      onClick={() => setOpenTaskId(t.id)} data-testid={`cc-task-${t.id}`}>
                      <td style={{ padding: '8px 4px', maxWidth: 340 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {t.label_color && <span style={{ width: 3, height: 20, borderRadius: 2, background: t.label_color }} />}
                          {!!t.completed && <Check size={13} color="#61BD4F" />}
                          <span style={{ fontWeight: 500, textDecoration: t.completed ? 'line-through' : 'none' }}>{t.title}</span>
                          {!!t.pending_delete && <AlertCircle size={12} color="#DE350B" />}
                        </div>
                      </td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.project_color }} />
                          {t.project_name}
                        </span>
                      </td>
                      <td>
                        {t.priority && (
                          <span className={`tm-pill tm-pill-${priorityKey(t)}`}>{PRIORITY_LABEL[priorityKey(t)]}</span>
                        )}
                      </td>
                      <td>
                        <div className="flex -space-x-1">
                          {(t.assignees || []).slice(0, 3).map((a) => <Avatar key={a.id} user={a} size={22} />)}
                          {(t.assignees || []).length === 0 && <span style={{ color: 'var(--trello-text-muted)', fontSize: 12 }}>—</span>}
                        </div>
                      </td>
                      <td>{dc ? <span className={dc}>{fmtDate(t.due_date)}</span> : <span style={{ color: 'var(--trello-text-muted)' }}>—</span>}</td>
                      <td>
                        {t.completed
                          ? <span className="tm-pill" style={{ background: 'var(--trello-list-bg)', color: '#006644' }}>Done</span>
                          : t.due_date && new Date(t.due_date) < new Date()
                            ? <span className="tm-pill tm-pill-now">Overdue</span>
                            : <span className="tm-pill" style={{ background: 'var(--trello-list-bg)', color: 'var(--trello-text-muted)' }}>Open</span>}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: 'var(--trello-text-muted)' }}>No tasks match your filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
            <div className="flex items-center gap-2 mb-3">
              <Users size={16} /><h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>By Manager</h3>
            </div>
            <div className="space-y-2">
              {byManager.map((m, i) => (
                <div key={i} data-testid={`mgr-${i}`}>
                  <div className="flex justify-between" style={{ fontSize: 13 }}>
                    <span style={{ fontWeight: 600 }}>{m.name}</span>
                    <span>{m.done}/{m.tasks}</span>
                  </div>
                  <div className="tm-mini-progress"><div style={{ width: m.tasks ? `${(m.done / m.tasks) * 100}%` : '0%' }} /></div>
                  <div style={{ fontSize: 11, color: 'var(--trello-text-muted)' }}>{m.count} project{m.count === 1 ? '' : 's'}</div>
                </div>
              ))}
              {byManager.length === 0 && <div style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>No managers yet.</div>}
            </div>
          </div>

          <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
            <div className="flex items-center gap-2 mb-3">
              <LayoutGrid size={16} /><h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Project progress</h3>
            </div>
            <div className="space-y-3">
              {progress.map((p) => (
                <Link to={`/projects/${p.project_id}`} key={p.project_id} data-testid={`cc-progress-${p.project_id}`}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                  <div className="flex justify-between" style={{ fontSize: 13 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || '#0079BF' }} />
                      {p.name}
                    </span>
                    <span>{p.pct_complete}%</span>
                  </div>
                  <div className="tm-progress-track"><div className="tm-progress-fill" style={{ width: `${p.pct_complete}%`, background: p.color || '#0079BF' }} /></div>
                </Link>
              ))}
              {progress.length === 0 && <div style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>No projects yet.</div>}
            </div>
          </div>

          {summary && (
            <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700 }}>Team snapshot</h3>
              <div className="grid grid-cols-2 gap-2" style={{ fontSize: 13 }}>
                <StatMini label="Projects" value={summary.total_projects} />
                <StatMini label="Tasks" value={summary.total_tasks} />
                <StatMini label="Completed" value={summary.completed_tasks} />
                <StatMini label="Active people" value={summary.active_members} />
              </div>
            </div>
          )}
        </div>
      </div>

      {openTaskId && (
        <TaskDetail taskId={openTaskId} currentUser={user}
          onClose={() => setOpenTaskId(null)} onChange={load} />
      )}

      {decisionModal && (
        <div className="tm-modal-overlay" style={{ zIndex: 10000 }} onClick={() => !decisionBusy && setDecisionModal(null)}>
          <div className="tm-modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()} data-testid="decision-modal">
            <div className="flex items-start justify-between mb-3">
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {decisionModal.mode === 'approve' ? 'Approve deletion' : 'Reject suggestion'}
              </h3>
              <button className="tm-btn-ghost" onClick={() => !decisionBusy && setDecisionModal(null)} data-testid="decision-close"><X size={18} /></button>
            </div>
            <div style={{ fontSize: 13, color: 'var(--trello-text-muted)', marginBottom: 6 }}>
              Task: <b style={{ color: 'var(--trello-text)' }}>{decisionModal.suggestion.task_title}</b>
            </div>
            <div style={{ fontSize: 12, color: 'var(--trello-text-muted)', marginBottom: 12 }}>
              {decisionModal.mode === 'approve'
                ? 'The task will be archived and the requester will earn +10 points.'
                : 'The task will be reopened (un-archived, marked in progress).'}
            </div>
            <div className="tm-label">Leader comment (optional)</div>
            <textarea className="tm-input" rows={3} placeholder="Add feedback for the requester…"
              value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)}
              data-testid="decision-comment" />
            <div className="flex justify-end gap-2 mt-4">
              <button className="tm-btn-ghost" onClick={() => setDecisionModal(null)} disabled={decisionBusy} data-testid="decision-cancel">Cancel</button>
              <button className="tm-btn"
                style={{ background: decisionModal.mode === 'approve' ? '#519839' : '#DE350B' }}
                disabled={decisionBusy}
                data-testid="decision-confirm"
                onClick={async () => {
                  setDecisionBusy(true);
                  try {
                    await api.patch(`/rewards/deletion-suggestions/${decisionModal.suggestion.id}`, {
                      status: decisionModal.mode === 'approve' ? 'approved' : 'rejected',
                      leader_comment: decisionComment.trim() || null,
                    });
                    setDecisionModal(null); setDecisionComment('');
                    load();
                  } catch (e) { alert(e.response?.data?.error || 'Failed'); }
                  finally { setDecisionBusy(false); }
                }}>
                {decisionBusy ? 'Saving…' : (decisionModal.mode === 'approve' ? 'Approve & archive' : 'Reject & reopen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, color, bg, testId, onClick, active, disabled, Icon }) {
  const clickable = !!onClick && !disabled;
  return (
    <button
      type="button"
      className="tm-stat-tinted"
      onClick={clickable ? onClick : undefined}
      disabled={disabled}
      data-disabled={disabled ? 'true' : 'false'}
      data-testid={testId}
      style={{
        '--tint': color,
        '--tint-bg': bg,
        outline: active ? `2px solid ${color}` : 'none',
        outlineOffset: active ? 2 : 0,
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={16} color={color} />}
        <span className="tm-stat-label">{label}</span>
      </div>
      <div className="tm-stat-value">{value}</div>
    </button>
  );
}

function StatMini({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
    </div>
  );
}

